
import { useState, useEffect } from 'react';
import { toast } from '@/hooks/use-toast';
import { Invoice, globalInvoices } from '@/components/InvoiceList';
import { calculateStripeFee, getChargeAchFeesSync } from '@/utils/fees';

export const useInvoiceViewer = (invoiceId: string | undefined) => {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'bank' | 'card'>('bank');
  const [expandedMethod, setExpandedMethod] = useState<'bank' | 'card' | null>('bank');
  const [chargeAchFees, setChargeAchFees] = useState<boolean>(false);

  // Get the ACH fee setting
  useEffect(() => {
    const achFeesSetting = getChargeAchFeesSync();
    setChargeAchFees(achFeesSetting);
  }, []);

  useEffect(() => {
    setLoading(true);

    const foundInvoice = globalInvoices.find(inv => inv.id === invoiceId);

    setTimeout(() => {
      if (foundInvoice) {
        setInvoice({
          ...foundInvoice,
          heroImage: localStorage.getItem('invoiceHeroImage') || 'business'
        });
      } else {
        setInvoice(null);
      }
      setLoading(false);
    }, 500);
  }, [invoiceId]);

  const handlePaymentMethodSelect = (method: 'bank' | 'card') => {
    setSelectedPaymentMethod(method);
    setExpandedMethod(method === expandedMethod ? null : method);
  };

  const handlePaymentSubmit = () => {
    toast({
      title: "Payment initiated",
      description: `Processing ${selectedPaymentMethod === 'bank' ? 'bank transfer' : 'credit card'} payment`,
    });
  };

  // Use the standard fee function
  const showProcessingFee = (method: 'bank' | 'card') => {
    if (method === 'card') return true;
    return method === 'bank' && chargeAchFees;
  };

  const getProcessingFee = (amount: number, method: 'bank' | 'card') => {
    if (showProcessingFee(method)) {
      return calculateStripeFee(amount, method);
    }
    return 0;
  };

  const hasSubscriptionItems = () => {
    if (!invoice || !invoice.lineItems) return invoice?.isSubscription || false;
    return invoice.lineItems.some(item => item.isSubscription);
  };

  return {
    invoice,
    loading,
    selectedPaymentMethod,
    expandedMethod,
    chargeAchFees,
    handlePaymentMethodSelect,
    handlePaymentSubmit,
    getProcessingFee,
    showProcessingFee,
    hasSubscriptionItems
  };
}
